import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useTranslation } from "react-i18next";
import { useSwipeable } from "react-swipeable";
import { ToastContainer, toast } from "react-toastify";
import TiktokPixel from "tiktok-pixel";
import { FavoriteButton } from "./favoriteButton";
import { useIsFavorite } from "@/api/traveler";
import arrowBack from "@/assets/images/icons/arrowBack.svg";
import share from "@/assets/images/icons/share.svg";
import whiteStar from "@/assets/images/icons/whiteStar.svg";
import HexagonButton from "@/common/customElements/HexagonButton";
import {
  AbsoluteDiv,
  CircularDiv,
  CustomDiv,
  HorizontalCenterDiv,
  InlineDiv,
} from "@/common/divs";
import { RegularText, SemiBoldSpanText, SemiBoldText } from "@/common/texts";
import FirebaseContext from "@/context/firebaseContext/firebaseContext";
import { useGoogleTranslation } from "@/hooks/useGoogleTranslation";
import { useTrack } from "@/hooks/useTrack";
import { ActivityDetailGeoSearch } from "@/model";
import { FirebaseContextType } from "@/types/components";
import {
  NEUTRO_TITLES_COLOR,
  PRIMARY_BRAND_VARIANT_COLOR,
  PRIMARY_BUTTON_COLOR,
} from "@/utils/constants";
import { renderSubCategoryIcon } from "@/utils/functions";
import "react-toastify/dist/ReactToastify.css";

interface ImageBackgroundWithCarouselProps {
  images: { link: string; name: string; type?: string }[];
  activity: ActivityDetailGeoSearch;
}

const ImageBackgroundWithCarousel: React.FC<
  ImageBackgroundWithCarouselProps
> = ({ images, activity }) => {
  const { data: session } = useSession();
  const numOfImages = images.length;
  const [selectedImage, setSelectedImage] = useState(0);
  const updateNotificationRef = useRef<HTMLElement | null>(null);
  const { data: isFavoriteResult, isLoading } = useIsFavorite(activity.id, {
    query: { enabled: session !== null },
  });
  const { t } = useTranslation();
  const { getTranslation, translation } = useGoogleTranslation();
  useEffect(() => {
    getTranslation(activity.name);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (selectedImage == images.length - 1) {
        setSelectedImage(0);
      } else {
        setSelectedImage((prevState) => prevState + 1);
      }
    },
    onSwipedRight: () => {
      if (selectedImage == 0) {
        setSelectedImage(images.length - 1);
      } else {
        setSelectedImage((prevState) => prevState - 1);
      }
    },
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });
  const isFavorite = useMemo(() => {
    return isFavoriteResult ?? false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavoriteResult, isLoading]);
  useEffect(() => {
    if (updateNotificationRef.current) {
      updateNotificationRef.current.animate(
        {
          opacity: [0.2, 1],
        },
        1500,
      );
    }
  }, [selectedImage]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedImage((prev) => (prev + 1) % numOfImages);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage]);
  const notify = () => toast(t("notifications.copied"));
  const { trackEvent, TrackCustomEvents } = useTrack();
  const { logAnalyticsEvent } = useContext(
    FirebaseContext,
  ) as FirebaseContextType;
  const router = useRouter();
  const getImageUrl = () => {
    if (images[selectedImage].link) {
      if (!images[selectedImage].link.includes("cdn.filestackcontent.com")) {
        return (
          images[selectedImage].link +
          `?height=${"450"}&format=webp&quality=100`
        );
      }
      return images[selectedImage].link.replace(
        "https://cdn.filestackcontent.com/",
        `https://cdn.filestackcontent.com/resize=height:${"450"}/`,
      );
    }
  };
  return (
    <CustomDiv
      //@ts-ignore
      ref={updateNotificationRef}
      className="fade-in-image-mobile"
      height={"450px"}
      style={{
        width: "100%",
        background: "linear-gradient(transparent, black)",
      }}
    >
      <img
        {...handlers}
        alt="background"
        src={getImageUrl()}
        fetchPriority="high"
        loading="eager"
        style={{
          width: "100%",
          height: "450px",
          opacity: "0.5",
          objectFit: "cover",
        }}
      />
      <AbsoluteDiv style={{ top: "10px", left: "10px" }}>
        <HexagonButton
          //onClick={actionToDo ? actionToDo() : window.history.back()}
          width="60px"
          backgroundColor={PRIMARY_BUTTON_COLOR}
          onClick={() => {
            if (
              window.history.length > 1 &&
              document.referrer.indexOf(window.location.host) !== -1
            ) {
              window.history.back();
            } else {
              router.push("/home");
            }
          }}
        >
          <Image
            width={34}
            height={34}
            alt="arrowBack"
            src={arrowBack.src}
            onClick={() => {
              window.history.back();
            }}
            style={{
              cursor: "pointer",
              marginTop: "-41px",
              marginLeft: "12px",
              position: "absolute",
              zIndex: "99",
            }}
          />
        </HexagonButton>
      </AbsoluteDiv>
      <AbsoluteDiv bottom="10px" width="100%">
        <div style={{ paddingLeft: "16px", paddingRight: "16px" }}>
          <InlineDiv style={{ gap: "8px" }}>
            <CircularDiv
              background={PRIMARY_BUTTON_COLOR}
              style={{ cursor: "pointer" }}
              onClick={() => {
                router.push(
                  "/home?veeSearch=true&community=" +
                    activity.community?.id +
                    "&city=" +
                    activity.city +
                    "&term=cat:" +
                    activity.category.id +
                    " " +
                    activity.category.name,
                );
              }}
              height="32px"
              paddingX="16px"
              width="fit-content"
            >
              <InlineDiv style={{ alignItems: "center", gap: "5px" }}>
                <SemiBoldSpanText
                  fontSize="14px"
                  color={NEUTRO_TITLES_COLOR}
                  style={{ marginTop: "-4px", lineHeight: "13px" }}
                >
                  {activity.category.name}
                </SemiBoldSpanText>
                <img
                  src={renderSubCategoryIcon(activity.category.id, 0)}
                  alt="subCategoryMarker"
                  style={{ width: "25px", height: "25px" }}
                ></img>
              </InlineDiv>
            </CircularDiv>
            {activity.subcategory && (
              <CircularDiv
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (activity.subcategory) {
                    router.push(
                      "/home?veeSearch=true&community=" +
                        activity.community?.id +
                        "&city=" +
                        activity.city +
                        "&term=" +
                        activity.subcategory.name,
                    );
                  }
                }}
                background={PRIMARY_BUTTON_COLOR}
                height="32px"
                paddingX="16px"
                width="fit-content"
              >
                <InlineDiv style={{ alignItems: "center", gap: "5px" }}>
                  <SemiBoldSpanText
                    fontSize="14px"
                    color={NEUTRO_TITLES_COLOR}
                    style={{ marginTop: "-4px", lineHeight: "13px" }}
                  >
                    {activity.subcategory.name}
                  </SemiBoldSpanText>
                  <img
                    src={renderSubCategoryIcon(
                      activity.category.id,
                      activity.subcategory.id ?? 0,
                    )}
                    alt="subCategoryMarker"
                    style={{ width: "25px", height: "25px" }}
                  ></img>
                </InlineDiv>
              </CircularDiv>
            )}
          </InlineDiv>
          <div>
            <SemiBoldText
              fontSize="18px"
              style={{ marginTop: "10px" }}
              color={PRIMARY_BRAND_VARIANT_COLOR}
            >
              {translation.length > 0 ? translation : activity.name}
            </SemiBoldText>
            <InlineDiv
              style={{
                justifyContent: "space-between",
                marginBottom: "5px",
                marginTop: "5px",
                minWidth: "150px",
              }}
            >
              <RegularText fontSize="24px" color={PRIMARY_BRAND_VARIANT_COLOR}>
                {t("activity.from")} {activity.priceWithTaxFormatted}
              </RegularText>
              {activity.rating ?
                activity.rating > 0 && (
                  <InlineDiv style={{ gap: "4px", marginTop: "3px" }}>
                    {Array.from({ length: activity.rating }).map((_, index) => (
                      <Image
                        key={index}
                        src={whiteStar.src}
                        alt="starIcon"
                        width={20}
                        height={20}
                        style={{ width: "20px", height: "20px" }}
                      />
                    ))}
                  </InlineDiv>
                )
              : null}
              <InlineDiv style={{ gap: "10px", marginTop: "4px" }}>
                {!isLoading ?
                  <FavoriteButton
                    id={activity.id}
                    isFavoriteActivity={isFavorite}
                    sx={{
                      width: "32px",
                      height: "32px",
                      // position: "absolute",
                    }}
                    activity={activity}
                  ></FavoriteButton>
                : null}
                <CustomDiv marginY="2px">
                  <HexagonButton
                    width="32px"
                    backgroundColor={PRIMARY_BUTTON_COLOR}
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      trackEvent({
                        name: TrackCustomEvents.EVENT_SHARED_PRODUCT,
                        properties: {
                          activity_id: activity.id,
                          activity_name: activity.name,
                          activity_category: activity.category,
                          activity_subcategory: activity.subcategory,
                        },
                      });
                      logAnalyticsEvent(
                        TrackCustomEvents.EVENT_SHARED_PRODUCT_WEB,
                        {
                          activity_id: activity.id,
                          activity_name: activity.name,
                          activity_category: activity.category,
                          activity_subcategory: activity.subcategory,
                        },
                      );
                      TiktokPixel.track("Share", {
                        activity_id: activity.id,
                        activity_name: activity.name,
                        activity_category: activity.category,
                        activity_subcategory: activity.subcategory,
                      });
                      notify();
                    }}
                  >
                    {" "}
                    <Image
                      alt="share"
                      width="16"
                      height="16"
                      src={share.src}
                      style={{
                        position: "absolute",
                        top: "22%",
                        left: "25%",
                      }}
                    />
                  </HexagonButton>
                </CustomDiv>
              </InlineDiv>
            </InlineDiv>
          </div>
        </div>
        <HorizontalCenterDiv style={{ gap: "10px" }}>
          {numOfImages > 1 &&
            Array.from({ length: numOfImages }).map((_, index) => (
              <CircularDiv
                key={index}
                width="12px"
                height="12px"
                background={
                  index == selectedImage ? PRIMARY_BUTTON_COLOR : "#D9D9D980"
                }
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedImage(index)}
              />
            ))}
        </HorizontalCenterDiv>
      </AbsoluteDiv>
      <ToastContainer></ToastContainer>
    </CustomDiv>
  );
};

export default ImageBackgroundWithCarousel;
